import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { getUrl, hasLink } from '@ngxp/rest';
import { VerfuegungFacade } from '@schir-int-client/verfuegung-shared';
import { map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import {
	clearAdressatenByAufgabeAction,
	clearSelectedAufgabeAction,
	createAufgabeAction,
	createAufgabeSuccessAction,
	deleteAufgabeAction,
	loadAdressatenByAufgabeAction,
	loadAufgabenByVerfuegungAction,
	loadAufgabenByVerfuegungSuccessAction,
	markAufgabeAsErledigtAction,
	markAufgabeAsFreigegebenAction,
	updateAufgabeAction,
	updateAufgabeSuccessAction,
} from './aufgabe.actions';
import { AufgabeFacade } from './aufgabe.facade';
import { AufgabeAnschreibenBody } from './aufgabe.model';
import { AufgabeService } from './aufgabe.service';
import {
	createKorrespondenzVorlageAction,
	loadKorrespondenzVorlageAction,
} from '../../../korrespondenz-shared/src/lib/korrespondenz-vorlage/korrespondenz-vorlage.actions';
import { KorrespondenzVorlage } from '../../../korrespondenz-shared/src/lib/korrespondenz-vorlage/korrespondenz-vorlage.model';
import { AufgabeLinkRel } from './aufgabe.linkrel';
import { VerfahrenFacade } from '@schir-int-client/verfahren-shared';

@Injectable()
export class AufgabeEffects {
	constructor(
		private actions: Actions,
		private aufgabeService: AufgabeService,
		private verfuegungFacade: VerfuegungFacade,
		private aufgabeFacade: AufgabeFacade,
		private verfahrenFacade: VerfahrenFacade
	) {}

	createAufgabe = createEffect(() =>
		this.actions.pipe(
			ofType(createAufgabeAction),
			withLatestFrom(this.verfuegungFacade.verfuegung),
			mergeMap(([{ aufgabe }, verfuegung]) => {
				const anschreibenBody: AufgabeAnschreibenBody = <AufgabeAnschreibenBody>aufgabe.body;

				if (anschreibenBody && anschreibenBody.vorlageId) {
					const korrespondenzVorlage: KorrespondenzVorlage = {
						rechtspfleger: anschreibenBody.rechtspfleger,
						datum: anschreibenBody.datum,
					};

					return this.aufgabeService.createAufgabe(verfuegung, aufgabe).pipe(
						mergeMap(aufgabe => [
							createAufgabeSuccessAction({ aufgabe }),
							createKorrespondenzVorlageAction({
								korrespondenzVorlage,
								vorlageId: anschreibenBody.vorlageId,
							}),
						])
					);
				} else {
					return this.aufgabeService
						.createAufgabe(verfuegung, aufgabe)
						.pipe(map(aufgabe => createAufgabeSuccessAction({ aufgabe })));
				}
			})
		)
	);

	updateAufgabe = createEffect(() =>
		this.actions.pipe(
			ofType(updateAufgabeAction),
			withLatestFrom(this.aufgabeFacade.aufgabe),
			switchMap(([{ aufgabe }, aufgabeToUpdate]) => {
				return this.aufgabeService.updateAufgabe(aufgabeToUpdate, aufgabe).pipe(
					mergeMap(aufgabe => {
						if (hasLink(aufgabe, AufgabeLinkRel.KORRESPONDENZ_VORLAGE)) {
							return [
								updateAufgabeSuccessAction({ aufgabe }),
								loadKorrespondenzVorlageAction({ aufgabe }),
							];
						}
						return [updateAufgabeSuccessAction({ aufgabe })];
					})
				);
			})
		)
	);

	loadAufgabenInVerfuegung = createEffect(() =>
		this.actions.pipe(
			ofType(loadAufgabenByVerfuegungAction),
			withLatestFrom(this.verfuegungFacade.verfuegung, this.verfahrenFacade.verfahrenSingle),
			mergeMap(([, verfuegung, verfahren]) => {
				return this.aufgabeService.getAufgaben(verfuegung).pipe(
					map(aufgaben =>
						loadAufgabenByVerfuegungSuccessAction({
							verfuegung,
							aufgaben,
							register: verfahren.register,
						})
					)
				);
			})
		)
	);

	markAsErledigt = createEffect(() =>
		this.actions.pipe(
			ofType(markAufgabeAsErledigtAction),
			withLatestFrom(this.verfuegungFacade.verfuegung, this.verfahrenFacade.verfahrenSingle),
			switchMap(([{ aufgabe }, verfuegung, verfahren]) => {
				return this.aufgabeService.markAsErledigt(aufgabe).pipe(
					mergeMap(aufgaben => [
						loadAufgabenByVerfuegungSuccessAction({
							verfuegung,
							aufgaben,
							register: verfahren.register,
						}),
						loadAdressatenByAufgabeAction({ aufgabe }),
					])
				);
			})
		)
	);

	markAsFreigegeben = createEffect(() =>
		this.actions.pipe(
			ofType(markAufgabeAsFreigegebenAction),
			withLatestFrom(this.verfuegungFacade.verfuegung, this.verfahrenFacade.verfahrenSingle),
			switchMap(([{ aufgabe }, verfuegung, verfahren]) => {
				return this.aufgabeService.markAsFreigegeben(aufgabe).pipe(
					mergeMap(aufgaben => [
						loadAufgabenByVerfuegungSuccessAction({
							verfuegung,
							aufgaben,
							register: verfahren.register,
						}),
						loadAdressatenByAufgabeAction({ aufgabe }),
					])
				);
			})
		)
	);

	deleteAufgabe = createEffect(() =>
		this.actions.pipe(
			ofType(deleteAufgabeAction),
			withLatestFrom(this.verfuegungFacade.verfuegung, this.verfahrenFacade.verfahrenSingle),
			switchMap(([{ aufgabe }, verfuegung, verfahren]) => {
				return this.aufgabeService.deleteAufgabe(aufgabe).pipe(
					mergeMap(aufgaben => [
						loadAufgabenByVerfuegungSuccessAction({
							verfuegung,
							aufgaben,
							register: verfahren.register,
						}),
						clearAdressatenByAufgabeAction({ aufgabeUri: getUrl(aufgabe) }),
						clearSelectedAufgabeAction(),
					])
				);
			})
		)
	);
}
